import { useMemo, useEffect, useRef, useCallback, memo } from 'react';
import { useDroppable } from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { flattenTree } from '@calculom/report-designer';
import { useDesignerStore } from '../store';
import { CanvasRow } from './CanvasRow';
import type { DropIndicator } from '../index';

interface CanvasProps {
  dropIndicator: DropIndicator | null;
}

function DropLine() {
  return (
    <div className="h-0.5 bg-blue-500 rounded-full mx-2 my-0.5 shadow-sm shadow-blue-300" />
  );
}

export const Canvas = memo(function Canvas({ dropIndicator }: CanvasProps) {
  const { nodes, columnConfig, selectedRowId, selectRow } = useDesignerStore();

  const flatNodes = useMemo(() => flattenTree(nodes), [nodes]);
  const _columns = columnConfig.default;

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const prevCountRef = useRef(flatNodes.length);

  const { setNodeRef, isOver } = useDroppable({
    id: 'canvas-droppable',
  });

  // Combined ref for droppable + scrolling
  const combinedRef = useCallback(
    (el: HTMLDivElement | null) => {
      setNodeRef(el);
      scrollRef.current = el;
    },
    [setNodeRef],
  );

  // Auto-scroll to bottom when rows are added
  useEffect(() => {
    if (flatNodes.length > prevCountRef.current && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
    prevCountRef.current = flatNodes.length;
  }, [flatNodes.length]);

  const rowIds = useMemo(() => flatNodes.map((n) => n.id), [flatNodes]);

  return (
    <div
      className="flex-1 flex flex-col overflow-hidden bg-white"
      onClick={() => selectRow(null)}
    >
      {/* Sortable rows area */}
      <div
        ref={combinedRef}
        className={`
          flex-1 overflow-y-auto p-4 pb-64 space-y-1
          ${isOver ? 'bg-blue-50 ring-2 ring-blue-300 ring-inset' : ''}
          ${flatNodes.length === 0 ? 'flex items-center justify-center' : ''}
        `}
        onClick={(e) => e.stopPropagation()}
      >
        {flatNodes.length === 0 ? (
          <div className="text-gray-400 text-sm italic text-center py-12">
            Drag components from the palette to build your report
          </div>
        ) : (
          <SortableContext items={rowIds} strategy={verticalListSortingStrategy}>
            {flatNodes.map((node) => (
              <div key={node.id}>
                {dropIndicator?.targetId === node.id && dropIndicator.position === 'before' && (
                  <DropLine />
                )}
                <CanvasRow
                  row={node.data}
                  rowId={node.rowId}
                  depth={node.depth}
                  isSelected={selectedRowId === node.id}
                />
                {dropIndicator?.targetId === node.id && dropIndicator.position === 'after' && (
                  <DropLine />
                )}
              </div>
            ))}
          </SortableContext>
        )}
      </div>
    </div>
  );
});
